/**
 * 알림 우측 사이드바 본문
 * 연동된 알림 API(/api/notifications/my)를 호출하여 DB에 적재된 알림 내역을 조회하여 표시합니다.
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useRightSidebar } from '../../../../assets/contexts/RightSidebarContext';
import { formatDateTimeWithWeekday } from '../../../../assets/utils/comm/dateUtils';
import { requestNotificationUnreadCountRefresh } from '../../../../assets/utils/comm/notificationUnreadCountEvents';

/** 알림 수신일시: yyyy-mm-dd(요일) hh:mm:ss (괄호 앞 공백 없음) */
function formatNotificationReceivedAt(raw) {
  const s = formatDateTimeWithWeekday(raw);
  if (!s) return '';
  return s.replace(' (', '(');
}

export const NotificationSidebarGolinkIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="notification-sidebar-golink-svg">
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </svg>
);

/** 새로고침 아이콘 (원형 화살표) */
const IconRefresh = () => (
  <svg className="notification-sidebar-refresh-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 2v6h-6" />
    <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
    <path d="M3 22v-6h6" />
    <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
  </svg>
);

/** 알림 유형(notiType) 표시용 벨 아이콘 — 목록·팝업 공용 */
export const NotiTypeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-bell" viewBox="0 0 16 16" aria-hidden="true" style={{ verticalAlign: 'middle', flexShrink: 0 }}>
    <path d="M8 16a2 2 0 0 0 2-2H6a2 2 0 0 0 2 2M8 1.918l-.797.161A4 4 0 0 0 4 6c0 .628-.134 2.197-.459 3.742-.16.767-.376 1.566-.663 2.258h10.244c-.287-.692-.502-1.49-.663-2.258C12.134 8.197 12 6.628 12 6a4 4 0 0 0-3.203-3.92zM14.22 12c.223.447.481.801.78 1H1c.299-.199.557-.553.78-1C2.68 10.2 3 6.88 3 6c0-2.42 1.72-4.44 4.005-4.901a1 1 0 1 1 1.99 0A5 5 0 0 1 13 6c0 .88.32 4.2 1.22 6" />
  </svg>
);

/** 읽음 상태: API readYn — 전체는 파라미터 생략 */
const READ_STATUS = {
  ALL: 'all',
  UNREAD: 'unread',
  READ: 'read',
};

/** 알림 내역 페이지 이동 — 목록 아이콘 */
const IconListHistory = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="notification-sidebar-history-link__icon-svg">
    <line x1="8" y1="6" x2="21" y2="6" />
    <line x1="8" y1="12" x2="21" y2="12" />
    <line x1="8" y1="18" x2="21" y2="18" />
    <line x1="3" y1="6" x2="3.01" y2="6" />
    <line x1="3" y1="12" x2="3.01" y2="12" />
    <line x1="3" y1="18" x2="3.01" y2="18" />
  </svg>
);

const IconChevronRightSm = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="notification-sidebar-history-link__chevron-svg">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const NotificationSidebarContent = () => {
  const { t } = useTranslation();
  const { isOpen, mode, close } = useRightSidebar();
  const isNotificationOpen = isOpen && mode === 'notification';
  const [notifications, setNotifications] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [readStatusFilter, setReadStatusFilter] = useState(READ_STATUS.ALL);
  const optionAllLabel = t('popup.notificationSidebarContent.filterTypeAll');
  const [filterType, setFilterType] = useState(optionAllLabel);

  /** 목록을 돌며 본 notiType 누적 — 필터 적용 시에도 콤보 옵션 유지 */
  const seenNotiTypesRef = useRef(new Set());

  /** 조회된 알림에서 유형(notiType) 목록 — 서버 필터와 맞추기 위해 누적 집합 사용 */
  const typeOptions = useMemo(() => {
    (notifications || []).forEach((n) => {
      if (n.notiType) seenNotiTypesRef.current.add(n.notiType);
    });
    return [optionAllLabel, ...Array.from(seenNotiTypesRef.current).sort()];
  }, [notifications, optionAllLabel]);

  const fetchNotifications = useCallback(async (isBackground = false) => {
    if (!isBackground) setIsLoading(true);
    try {
      const params = { limit: 10 };
      if (readStatusFilter === READ_STATUS.UNREAD) {
        params.readYn = 'N';
      } else if (readStatusFilter === READ_STATUS.READ) {
        params.readYn = 'Y';
      }
      if (filterType !== optionAllLabel) {
        params.notiType = filterType;
      }

      const res = await axios.get('/api/notifications/my/recent', { params });

      if (res.data) {
        // recent API는 List<CetusNotification>을 직접 반환하므로 res.data 자체가 배열입니다.
        setNotifications(res.data || []);
        setTotalItems((res.data || []).length);
      }
    } catch (error) {
      console.error('알림 조회 실패:', error);
    } finally {
      if (!isBackground) setIsLoading(false);
    }
  }, [readStatusFilter, filterType, optionAllLabel]);

  useEffect(() => {
    // 사이드바가 실제로 열렸을 때만 목록 조회
    if (!isNotificationOpen) return;
    fetchNotifications();
  }, [isNotificationOpen, fetchNotifications]);

  // 알림 읽음 처리
  const handleMarkAsRead = async (uid) => {
    try {
      await axios.put(`/api/notifications/${uid}/read`, null, { withCredentials: true });
      fetchNotifications();
      requestNotificationUnreadCountRefresh();
    } catch (error) {
      console.error('알림 읽음 처리 실패:', error);
    }
  };

  return (
    <div className="notification-sidebar-content">
      <div className="notification-sidebar-section-head d-flex justify-content-between align-items-center gap-1 flex-nowrap">
        <div className="notification-sidebar-count-row d-flex align-items-center gap-1 flex-nowrap flex-shrink-0">
          <span className="notification-sidebar-section-count text-muted">{t('popup.notificationSidebarContent.totalCount', { count: totalItems })}</span>
          <button type="button" className="notification-sidebar-refresh-btn" onClick={() => fetchNotifications()} disabled={isLoading} aria-label={t('popup.notificationSidebarContent.refresh')}>
            <IconRefresh />
          </button>
        </div>
        <div className="notification-sidebar-filters d-flex align-items-center gap-1 flex-nowrap justify-content-end flex-shrink-0">
          <select
            className="notification-sidebar-filter-combo form-select form-select-sm"
            value={readStatusFilter}
            onChange={(e) => {
              setReadStatusFilter(e.target.value);
            }}
            aria-label={t('popup.notificationSidebarContent.readStatusFilterAria')}
          >
            <option value={READ_STATUS.ALL}>{t('popup.notificationSidebarContent.readStatusAll')}</option>
            <option value={READ_STATUS.UNREAD}>{t('popup.notificationSidebarContent.readStatusUnread')}</option>
            <option value={READ_STATUS.READ}>{t('popup.notificationSidebarContent.readStatusRead')}</option>
          </select>
          <select
            className="notification-sidebar-filter-combo form-select form-select-sm"
            value={filterType}
            onChange={(e) => {
              setFilterType(e.target.value);
            }}
            aria-label={t('popup.notificationSidebarContent.typeFilterAria')}
          >
            {typeOptions.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="notification-sidebar-history-row d-flex justify-content-end align-items-center">
        <Link
          to="/mypage/activity-history/notifications"
          className="notification-sidebar-history-link"
          onClick={close}
          aria-label={t('popup.notificationSidebarContent.historyAria')}
        >
          <IconListHistory />
          <span className="notification-sidebar-history-link__text">{t('popup.notificationSidebarContent.history')}</span>
          <IconChevronRightSm />
        </Link>
      </div>

      <div className="notification-sidebar-role-body">
        {isLoading ? (
          <div className="text-center py-4 text-muted">{t('popup.notificationSidebarContent.loading')}</div>
        ) : notifications.length === 0 ? (
          <div className="text-center py-5 text-muted">{t('popup.notificationSidebarContent.empty')}</div>
        ) : (
          <ul className="notification-sidebar-card-list list-unstyled m-0 d-flex flex-column gap-2">
            {notifications.map((item) => {
              const isUnread = item.readYn === 'N';
              const receivedAtLabel = formatNotificationReceivedAt(item.regDt);
              return (
                <li
                  key={item.uid}
                  className={`notification-sidebar-card-item border rounded ${isUnread ? 'bg-light' : ''}`}
                  style={{
                    transition: 'all 0.2s',
                    borderColor: isUnread ? '#cce5ff' : '#e5e7eb',
                    padding: '8px 16px'
                  }}>
                  <div className="d-flex w-100 justify-content-between align-items-center">
                    <div className="d-flex flex-wrap align-items-center gap-2">
                      <span className={`notification-sidebar-icon-wrap${isUnread ? ' has-new' : ''}`}>
                        <NotiTypeIcon />
                        {isUnread && <span className="notification-sidebar-icon-new-n" aria-hidden="true">N</span>}
                      </span>
                      <span className="text-dark fw-bold" style={{ fontSize: '13px', lineHeight: '1' }}>{item.notiType}</span>
                    </div>
                    <div className="d-flex align-items-center gap-1 ms-2 flex-shrink-0">
                      {isUnread ? (
                        <button type="button" className="btn btn-light border notification-sidebar-card-action-btn" onClick={() => handleMarkAsRead(item.uid)}>{t('popup.notificationSidebarContent.markAsRead')}</button>
                      ) : (
                        <span className="text-muted notification-sidebar-card-action-label">{t('popup.notificationSidebarContent.read')}</span>
                      )}
                      {item.linkUrl && (
                        <Link
                          to={item.linkUrl}
                          className="btn btn-primary text-white notification-sidebar-card-action-btn"
                          onClick={() => { if (isUnread) handleMarkAsRead(item.uid); }}
                        >
                          {t('popup.notificationSidebarContent.view')}
                        </Link>
                      )}
                    </div>
                  </div>
                  <p className="notification-sidebar-card-msg mb-0 mt-1" style={{ fontSize: '13px', color: isUnread ? '#000' : '#4b5563', lineHeight: '1.4' }} dangerouslySetInnerHTML={{ __html: item.message }}></p>
                  {receivedAtLabel ? (
                    <p className="notification-sidebar-card-regdt mb-0 mt-1 text-muted text-end" style={{ fontSize: '11px', lineHeight: '1.35' }}>
                      {receivedAtLabel}
                    </p>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* 최근 알림(recent) 조회 시에는 페이지네이션을 사용하지 않으므로 임시 숨김 처리 */}
      <style>{`
        .notification-sidebar-icon-wrap {
          position: relative;
          display: inline-flex;
          flex-shrink: 0;
        }
        .notification-sidebar-icon-new-n {
          position: absolute;
          top: -4px;
          right: -4px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 14px;
          height: 14px;
          border-radius: 50%;
          background: #dc3545;
          color: #fff;
          font-size: 9px;
          font-weight: 700;
          line-height: 1;
        }
        .notification-sidebar-card-action-btn,
        .notification-sidebar-card-action-label {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 52px;
          height: 20px;
          padding: 0 10px;
          font-size: 11px;
          white-space: nowrap;
          box-sizing: border-box;
        }
        .notification-sidebar-card-action-btn {
          border-radius: 4px;
        }
        .notification-sidebar-section-head {
          min-width: 0;
        }
        .notification-sidebar-section-count {
          font-size: 12px;
        }
        .notification-sidebar-history-link {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          flex-shrink: 0;
          padding: 0.25rem 0.45rem 0.25rem 0.4rem;
          border-radius: 6px;
          border: 1px solid color-mix(in srgb, var(--theme-color-primary, #e97730) 45%, #d1d5db);
          background: color-mix(in srgb, var(--theme-color-primary, #e97730) 10%, #ffffff);
          color: var(--theme-color-primary, #c45f1a);
          font-size: 12px;
          font-weight: 600;
          line-height: 1.2;
          text-decoration: none !important;
          white-space: nowrap;
          transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
        }
        .notification-sidebar-history-link:hover {
          background: color-mix(in srgb, var(--theme-color-primary, #e97730) 18%, #ffffff);
          border-color: var(--theme-color-primary, #e97730);
          color: var(--theme-color-primary, #e97730);
        }
        .notification-sidebar-history-link:focus-visible {
          outline: 2px solid var(--theme-color-primary, #e97730);
          outline-offset: 2px;
        }
        .notification-sidebar-history-link__icon-svg,
        .notification-sidebar-history-link__chevron-svg {
          flex-shrink: 0;
          opacity: 0.92;
        }
        .notification-sidebar-history-link__chevron-svg {
          opacity: 0.75;
        }
        .notification-sidebar-history-link:hover .notification-sidebar-history-link__chevron-svg {
          opacity: 1;
        }
        .notification-sidebar-history-row {
          width: 100%;
          margin-top: 2px;
          margin-bottom: 8px;
          padding-top: 2px;
        }
        .notification-sidebar-filters {
          min-width: 0;
        }
        .notification-sidebar-filter-combo {
          width: 100px;
          max-width: 100px;
          min-width: 0;
          flex: 0 0 100px;
          font-size: 13px;
          line-height: 1.2;
          padding: 0.2rem 1.25rem 0.2rem 0.3rem;
        }
        .notification-sidebar-card-list {
          max-height: 80vh;
          overflow-y: auto;
        }
        .notification-sidebar-refresh-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          padding: 0;
          border: none;
          border-radius: 0;
          background: transparent;
          color: #6b7280;
          cursor: pointer;
          transition: color 0.2s ease;
        }
        .notification-sidebar-refresh-btn:hover:not(:disabled) {
          color: #111827;
        }
        .notification-sidebar-refresh-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .notification-sidebar-refresh-icon {
          display: block;
          width: 20px;
          height: 20px;
        }
        .notification-sidebar-card-msg b,
        .notification-sidebar-card-msg strong {
          display: inline-block;
          margin-bottom: 0.35em;
        }
      `}
      </style>
    </div>
  );
};

export default NotificationSidebarContent;
